/**
 * Bulk date upload modal.
 *
 * EXPEDITING-3 (Aaron, 30 Sep 2026): each template carries only its OWN date.
 * CR-7778 (Sarah, 2 Oct 2026): the customer has two separate uploads.
 *   Supplier login:            Latest Supplier Promise Date (+ price, qty;
 *                              Comment required) → a supplier request.
 *   Upload supplier updates:   the SUPPLIER template, uploaded by the customer
 *                              on the supplier's behalf (Comment optional)
 *                              → a supplier request for the customer to answer.
 *   Upload customer updates:   Customer Required Date (Comment optional; no
 *                              price or qty) → set directly on a NEW line,
 *                              otherwise a request for the supplier to answer.
 * Rows that are not applied are listed with the reason and a link to the line.
 */
import { useState } from 'react';
import { Loader2, Download, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { purchasingAPI } from '../../../lib/api';
import { toast } from 'sonner';
import { usePortalMode } from '../../../hooks/usePortalMode';
import FileDropZone from '../../common/FileDropZone';
import { CUSTOMER_REQUIRED_DATE, SUPPLIER_PROMISE_DATE } from '../../../config/dateLabels';

/** CR-7778: which of the customer's two uploads this is. */
export type ImportPromiseDatesMethod = 'supplier_updates' | 'customer_updates';

/** CR-7778: one row that was not applied, with the line to open. */
interface SkippedRow {
  row: number;
  po_number: string;
  line: number | null;
  po_line_id: number | null;
  reason: string;
}

/** CR-7066: one rejected row, numbered as it appears in the spreadsheet. */
interface ImportRowError {
  /** Spreadsheet row number — the header is row 1, so data starts at 2. */
  row: number;
  po_number: string;
  line: number | null;
  message: string;
}

interface ImportResult {
  total:   number;
  updated: number;
  ignored: number;
  errors:  number;
  /** Absent on responses from before per-row reporting existed. */
  row_errors?: ImportRowError[];
  /** Failures counted but not detailed, when there are more than the cap. */
  row_errors_truncated?: number;
  /** CR-7778: every row that changed something and was not applied. */
  skipped?: number;
  skipped_rows?: SkippedRow[];
  skipped_rows_truncated?: number;
}

interface ImportPromiseDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  /** Current view/filter params — template will match exactly what's in the table */
  exportParams?: Record<string, string>;
  /** Total count of lines matching current filters — shown in the instructions */
  totalCount?: number;
  /** CR-7778: the customer's chosen upload. Ignored for a supplier. */
  method?: ImportPromiseDatesMethod;
}

export default function ImportPromiseDatesModal({
  isOpen,
  onClose,
  onImportComplete,
  exportParams = {},
  totalCount,
  method = 'customer_updates',
}: ImportPromiseDatesModalProps) {
  const { isSupplier } = usePortalMode();
  // CR-7778: the supplier's template is used by the supplier, and by the
  // customer uploading the supplier's updates on its behalf.
  const onBehalf = !isSupplier && method === 'supplier_updates';
  const supplierTemplate = isSupplier || onBehalf;
  // One name per template: the supplier updates template is "Upload supplier
  // updates" from either login (CR-7778).
  const title = supplierTemplate ? 'Upload supplier updates' : 'Upload customer updates';

  const [downloading, setDownloading] = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result,  setResult]  = useState<ImportResult | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [importDone, setImportDone] = useState(false);

  // ── Download Template ───────────────────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await purchasingAPI.exportLines({
        ...exportParams,
        export_type: 'import_template',
        ...(onBehalf ? { upload_method: 'supplier_updates' } : {}),
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href     = url;
      a.download = `${supplierTemplate ? 'supplier_updates' : 'customer_updates'}_template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch {
      toast.error('Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  // ── Upload & Import ─────────────────────────────────────────────────────────
  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (!isSupplier) formData.append('method', method);
      const response = await purchasingAPI.importPromiseDates(formData);
      const data = response.data;
      setResult(data);
      if (data.updated > 0) {
        toast.success(`${data.updated} line${data.updated !== 1 ? 's' : ''} updated`);
        setImportDone(true);
      } else {
        toast.info('No lines were updated');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string; detail?: string } } };
      const msg =
        axiosErr?.response?.data?.error ||
        axiosErr?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Upload failed. Please check the file format.');
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  // ── Close / reset ───────────────────────────────────────────────────────────
  const handleClose = () => {
    // Refresh the table if import was successful
    if (importDone) {
      onImportComplete();
      setImportDone(false);
    }
    setSelectedFile(null);
    setResult(null);
    setErrorMsg(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Download a pre-filled template containing your current PO lines, update the editable columns, then upload back.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">

          {/* CR-7778: the screen says plainly which upload is in use. */}
          {!isSupplier && (
            <div
              className={`rounded-md border px-4 py-2 text-sm font-medium ${onBehalf ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-sky-50 border-sky-300 text-sky-900'}`}
              data-testid="import-method-banner"
            >
              {onBehalf
                ? 'You are uploading the SUPPLIER\'s updates on the supplier\'s behalf.'
                : 'You are uploading YOUR OWN customer updates.'}
            </div>
          )}

          {/* Instructions */}
          <div className="rounded-md bg-muted/50 border border-border px-4 py-3 text-sm space-y-1">
            <p><span className="font-medium">Step 1:</span> Download the {supplierTemplate ? 'supplier updates ' : 'customer updates '}template. It will contain all {totalCount ? totalCount.toLocaleString() : ''} PO lines matching your current view and filters.</p>
            {supplierTemplate ? (
              <p><span className="font-medium">Step 2:</span> Edit the yellow-highlighted columns: <span className="font-semibold">{SUPPLIER_PROMISE_DATE}</span>, <span className="font-semibold">Proposed Unit Price</span>, <span className="font-semibold">Proposed Qty Ordered</span>, and <span className="font-semibold">Comment</span>.{' '}
                {onBehalf
                  ? 'Each change becomes a supplier change request, as if the supplier had uploaded it, for you to accept or reject. The comment is optional.'
                  : 'Each change goes to the customer to accept or reject.'}
              </p>
            ) : (
              <p><span className="font-medium">Step 2:</span> Edit the yellow-highlighted columns: <span className="font-semibold">{CUSTOMER_REQUIRED_DATE}</span> and <span className="font-semibold">Comment</span>. On a New line the dates are updated directly; on any other line the change goes to the supplier to accept or reject.</p>
            )}
            <p><span className="font-medium">Step 3:</span> Upload the file. Only rows with changes are processed, and each change is logged in the line&apos;s audit history.</p>
            <p className="text-muted-foreground mt-1">Closed, cancelled, despatched, received, on hold, declined and queried lines are skipped. A date before today is not accepted. Skipped rows are listed after the upload with the reason.</p>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="flex items-center gap-2"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download Template
            </Button>

          </div>

          {/* Customer review 11 Sep (drag and drop everywhere). */}
          <FileDropZone
            size="compact"
            accept=".xlsx,.xls,.csv"
            disabled={uploading}
            hint="Excel (.xlsx, .xls) or CSV"
            onFiles={(files) => { setSelectedFile(files[0]); setResult(null); setErrorMsg(null); }}
            data-testid="import-promise-dates-drop-zone"
          />

          {/* Supplier hint: comment required */}
          {isSupplier && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
              If the promise date is changed, please ensure a reason is entered in the{' '}
              <span className="font-semibold">Comment</span> column. Rows without a comment will
              not be imported.
            </div>
          )}

          {/* Selected file */}
          {selectedFile && !uploading && !result && (
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
              <span className="font-medium truncate">{selectedFile.name}</span>
              <div className="flex items-center gap-2 ml-2 shrink-0">
                <Button size="sm" onClick={handleUpload} disabled={uploading}>
                  {uploading
                    ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Uploading…</>
                    : 'Import'}
                </Button>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="p-0.5 hover:bg-muted rounded"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>
          )}

          {/* Uploading progress */}
          {uploading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Parsing data, please wait…
            </div>
          )}

          {/* Error message */}
          {errorMsg && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              {errorMsg}
            </div>
          )}

          {/* Results table */}
          {result && (
            <div className="rounded-lg border border-border overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 bg-muted/50 border-b border-border">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-semibold">Import complete</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-2 font-medium">Count</th>
                    <th className="text-left px-4 py-2 font-medium">Updated</th>
                    <th className="text-left px-4 py-2 font-medium">Ignored</th>
                    {result.errors > 0 && (
                      <th className="text-left px-4 py-2 font-medium text-red-600">Errors</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-4 py-3">{result.total}</td>
                    <td className="px-4 py-3 text-green-700 font-medium">{result.updated}</td>
                    <td className="px-4 py-3 text-muted-foreground">{result.ignored}</td>
                    {result.errors > 0 && (
                      <td className="px-4 py-3 text-red-600 font-medium">{result.errors}</td>
                    )}
                  </tr>
                </tbody>
              </table>
              {/* CR-7778: every row that was not applied, with the reason and
                  a link to the line (new tab, so these results stay open). */}
              {result.skipped_rows && result.skipped_rows.length > 0 ? (
                <div className="border-t border-border" data-testid="import-skipped-rows">
                  <div className="px-4 py-2 text-xs font-medium text-red-700 bg-red-50">
                    {result.skipped} row{result.skipped !== 1 ? 's were' : ' was'} skipped
                  </div>
                  <ul className="max-h-64 overflow-y-auto divide-y divide-border">
                    {result.skipped_rows.map((skippedRow, index) => (
                      <li key={`${skippedRow.row}-${index}`} className="px-4 py-2 text-xs">
                        <span className="font-medium">Row {skippedRow.row}</span>
                        {skippedRow.po_number && (
                          <span className="text-muted-foreground">
                            {', PO '}
                            {skippedRow.po_line_id ? (
                              <a
                                href={`/purchasing?tab=po_lines&line=${skippedRow.po_line_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                {skippedRow.po_number}
                                {skippedRow.line !== null && ` line ${skippedRow.line}`}
                              </a>
                            ) : (
                              <>
                                {skippedRow.po_number}
                                {skippedRow.line !== null && ` line ${skippedRow.line}`}
                              </>
                            )}
                          </span>
                        )}
                        <div className="text-red-700 mt-0.5">{skippedRow.reason}</div>
                      </li>
                    ))}
                  </ul>
                  {result.skipped_rows_truncated ? (
                    <div className="px-4 py-2 border-t border-border text-xs text-muted-foreground">
                      …and {result.skipped_rows_truncated} more skipped row
                      {result.skipped_rows_truncated !== 1 ? 's' : ''}.
                    </div>
                  ) : null}
                </div>
              ) : result.row_errors && result.row_errors.length > 0 && (
                /* CR-7066: responses from before CR-7778 carry only row_errors. */
                <div className="border-t border-border">
                  <div className="px-4 py-2 text-xs font-medium text-red-700 bg-red-50">
                    These rows were not imported
                  </div>
                  <ul className="max-h-48 overflow-y-auto divide-y divide-border">
                    {result.row_errors.map((rowError, index) => (
                      <li key={`${rowError.row}-${index}`} className="px-4 py-2 text-xs">
                        <span className="font-medium">Row {rowError.row}</span>
                        {rowError.po_number && (
                          <span className="text-muted-foreground">
                            {', PO '}{rowError.po_number}
                            {rowError.line !== null && ` line ${rowError.line}`}
                          </span>
                        )}
                        <div className="text-red-700 mt-0.5">{rowError.message}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
